import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LandingHeader from '../components/LandingHeader';
import LandingFooter from '../components/LandingFooter';
import '../styles/pages/LandingPage.scss';

const LandingPage = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const handleGetStarted = () => {
        if (currentUser) {
            // ログイン済みの場合はダッシュボードへ
            navigate(`/dashboard/${currentUser.uid}`);
        } else {
            // 未ログインの場合はログインページへ
            navigate('/login');
        }
    };

    return (
        <>
            <Helmet>
                <title>Suisui - シンプルで簡単AI支援のウェブサイト作成</title>
                <meta name="description" content="Suisuiは、誰でも簡単に美しいウェブサイトを作成できるCMSです。サブドメインでの公開、リアルタイムプレビュー、SEO最適化など、充実した機能を提供します。" />
                <meta name="keywords" content="CMS,ウェブサイト作成,サブドメイン,SEO,リアルタイムプレビュー,コンテンツ管理" />
                <meta property="og:title" content="Suisui - シンプルで美しいウェブサイト作成CMS" />
                <meta property="og:description" content="誰でも簡単に美しいウェブサイトを作成できるCMSです。" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://suisui.life" />
                <link rel="canonical" href="https://suisui.life" />
            </Helmet>

            <div className="landing-page">
                {/* ヘッダー */}
                <LandingHeader />

                {/* メインビジュアル */}
                <section className="hero">
                    <div className="container">
                        <div className="hero-content">
                            <h1 className="hero-title">
                                シンプルで簡単<br />
                                AI支援のウェブサイト作成
                            </h1>
                            <p className="hero-description">
                                Suisuiは、技術的な知識がなくても誰でも簡単に<br />
                                プロフェッショナルなウェブサイトを作成・公開できるCMSです。
                            </p>
                            <div className="hero-actions">
                                <button
                                    onClick={handleGetStarted}
                                    className="primary-button"
                                >
                                    無料で始める
                                </button>
                                <a href="#features" className="secondary-button">
                                    機能を見る
                                </a>
                            </div>
                        </div>
                        <div className="hero-visual">
                            <div className="browser-mockup">
                                <div className="browser-bar">
                                    <div className="browser-buttons">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                    <div className="browser-url">yoursite.suisui.life</div>
                                </div>
                                <div className="browser-content">
                                    <div className="sample-page">
                                        <h2>あなたのウェブサイト</h2>
                                        <p>美しいデザインで情報を発信</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 機能紹介 */}
                <section id="features" className="features">
                    <div className="container">
                        <h2 className="section-title">充実した機能</h2>
                        <div className="features-grid">
                            <div className="feature-card">
                                <div className="feature-icon">🌐</div>
                                <h3>サブドメイン公開</h3>
                                <p>yourname.suisui.lifeの形式で、独自のサブドメインでウェブサイトを公開できます。</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">⚡</div>
                                <h3>リアルタイムプレビュー</h3>
                                <p>編集内容をリアルタイムで確認しながら、直感的にコンテンツを作成できます。</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">🎨</div>
                                <h3>カスタムデザイン</h3>
                                <p>HTML/CSS/JavaScriptを使って、完全にカスタマイズされたデザインを実現できます。</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">📱</div>
                                <h3>レスポンシブ対応</h3>
                                <p>モバイル、タブレット、デスクトップすべてのデバイスで美しく表示されます。</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">🔍</div>
                                <h3>SEO最適化</h3>
                                <p>メタタグ、JSON-LD、サイトマップなど、検索エンジン最適化機能が充実しています。</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">📊</div>
                                <h3>アナリティクス</h3>
                                <p>Google Analytics連携で、サイトの訪問者数やユーザー行動を分析できます。</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 使い方 */}
                <section className="how-it-works">
                    <div className="container">
                        <h2 className="section-title">簡単3ステップ</h2>
                        <div className="steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <h3>アカウント作成</h3>
                                <p>無料でアカウントを作成し、お好きなサブドメインを選択します。</p>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <h3>コンテンツ作成</h3>
                                <p>直感的なエディターでページを作成・編集します。</p>
                            </div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <h3>公開・共有</h3>
                                <p>ワンクリックでウェブサイトを公開し、世界中に情報を発信します。</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA セクション */}
                <section className="cta">
                    <div className="container">
                        <div className="cta-content">
                            <h2>今すぐ始めましょう</h2>
                            <p>無料でアカウントを作成して、あなたのウェブサイトを作り始めましょう。</p>
                            <button
                                onClick={handleGetStarted}
                                className="primary-button"
                            >
                                無料で始める
                            </button>
                        </div>
                    </div>
                </section>

                {/* フッター */}
                <LandingFooter />
            </div>
        </>
    );
};

export default LandingPage;
